import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";

// Floating Help button — mirrors FeedbackButton's look and behavior, stacked directly above it.
// Clicking opens a small panel of common tasks; each expands to a short how-to. A Docs button at
// the bottom links to the full documentation.

type HelpTopic = {
  title: string;
  steps: string[];
  link?: { to: string; label: string };
};

const TOPICS: HelpTopic[] = [
  {
    title: "Install a skill",
    steps: [
      "Open a skill, copy its install prompt/command.",
      "Paste it into your terminal or agent.",
      "It lands in your skills folder, ready to use.",
    ],
    link: { to: "/skills", label: "Browse skills" },
  },
  {
    title: "Add your own skill",
    steps: [
      "Upload a folder, .tar, or .zip with SKILL.md.",
      "Set its domains and visibility.",
      "Submit — it passes Scan → Security, then goes live.",
    ],
    link: { to: "/skills/new", label: "Open Add a skill" },
  },
  {
    title: "Suggest an improvement",
    steps: [
      "Open Suggest on a skill's page.",
      "Describe the change or upload an edited archive.",
      "Process and Approve the suggestion from your dashboard.",
    ],
  },
  {
    title: "Use from your agent",
    steps: [
      "Grab the one-line setup from Settings → API Keys.",
      "Run it to connect your agent and install SkillPatch.",
      "Use /skillpatch to browse, install, or publish.",
    ],
    link: { to: "/settings", label: "Open Settings" },
  },
];

export default function HelpButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  if (!user) return null;

  function reset() {
    setExpanded(null);
    setOpen(false);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Help"
        className="fixed bottom-[7.5rem] right-6 z-40 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-lg transition-all hover:opacity-80"
        style={{ background: "var(--bg-elev)", borderColor: "var(--border-strong)", color: "var(--text-secondary)" }}
      >
        <HelpIcon size={15} />
        Help
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-6" onClick={reset}>
          <div
            className="w-full max-w-sm rounded-2xl p-5 shadow-2xl"
            style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-accent">
                  <HelpIcon size={16} />
                </span>
                <h2 className="font-display text-base font-semibold" style={{ color: "var(--text)" }}>Help</h2>
              </div>
              <button onClick={reset} className="transition-opacity hover:opacity-60" style={{ color: "var(--text-muted)" }} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mb-3 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Explore guides for common tasks, or browse the full documentation below.
            </p>

            <div className="flex flex-col gap-1.5">
              {TOPICS.map((topic, i) => {
                const isOpen = expanded === i;
                return (
                  <div
                    key={topic.title}
                    className="rounded-xl border transition-colors"
                    style={{
                      background: isOpen ? "var(--bg-subtle)" : "transparent",
                      borderColor: isOpen ? "var(--brand)" : "var(--border-strong)",
                    }}
                  >
                    <button
                      onClick={() => setExpanded(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-left transition hover:bg-black/[0.04] dark:hover:bg-white/[0.05]"
                    >
                      <span className="flex-1 text-sm font-medium" style={{ color: "var(--text)" }}>{topic.title}</span>
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        style={{ color: "var(--text-muted)" }}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="pb-3 pl-2.5 pr-3">
                        <ol className="list-decimal space-y-1 pl-4 text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                          {topic.steps.map((s, j) => <li key={j}>{s}</li>)}
                        </ol>
                        {topic.link && (
                          <Link
                            to={topic.link.to}
                            onClick={reset}
                            className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-primary transition hover:opacity-80"
                          >
                            {topic.link.label}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Link
              to="/docs"
              onClick={reset}
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[var(--border-strong)] px-4 py-2 text-sm font-semibold text-text transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              Browse the docs
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

function HelpIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}
